// Pure helpers for finding URLs in plain text so draft previews can render
// them as real, testable links (the "I cannot test links in the draft" fix).

/** One run of text — a bare segment, or a URL with its open-able href. */
export interface LinkSegment {
  /** The exact characters from the source text (what should be displayed). */
  text: string;
  /** A normalized, open-able URL when this segment is a link; null for text. */
  href: string | null;
}

// http(s) URLs plus bare "www." hosts (mail bodies are full of both).
const URL_RE = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

// Sentence punctuation that trails a pasted URL ("see https://x.com/y.") is
// almost never part of it — peel it off so the link resolves.
const TRAILING_PUNCT_RE = /[.,;:!?'"”’)\]}]+$/;

/**
 * Make a bare href open-able from the app's origin: URLs without a scheme
 * (e.g. "www.chemmasters.net/products") would otherwise resolve as relative
 * paths. Scheme-qualified hrefs (https:, mailto:, …) pass through untouched.
 */
export function normalizeHref(href: string): string {
  const trimmed = href.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * Split plain text into display segments, marking every URL with a
 * normalized href. The concatenation of `text` across segments always
 * equals the input — nothing is dropped or rewritten in the display copy.
 */
export function splitLinkSegments(text: string): LinkSegment[] {
  const segments: LinkSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    const url = m[0].replace(TRAILING_PUNCT_RE, '');
    if (!url) continue;
    if (start > last) segments.push({ text: text.slice(last, start), href: null });
    segments.push({ text: url, href: normalizeHref(url) });
    last = start + url.length;
  }
  if (last < text.length) segments.push({ text: text.slice(last), href: null });
  return segments;
}
